import { EventEmitter, Injectable, Output } from '@angular/core';
import { Direction, Directionality } from '@angular/cdk/bidi';
import { TranslateService } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, throwError } from 'rxjs';
import { catchError, filter, first, map, startWith, take } from 'rxjs/operators';
import { ConfirmationService } from '@shared/services/confirmation.service';
import { Confirmation, ConfirmationType } from '@shared/models/confirmation.model';
import { Language } from '@shared/models/language.model';
import { LanguageServiceDelegateInterface } from '../interfaces/language-service-delegate.interface';
import { ConfigConstant } from '@shared/constants/config.constant';
import { LanguageEnum } from '@shared/enums/global.enum';
import { LayoutService } from '../../layout/services/layout.service';
import {Locales} from "@shared/models/locales.model";
import { AppError } from '@shared/models';

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  @Output() languageChanged = new EventEmitter<Language>();
  private translationsList: any = {};

  // public fields
  currentLanguage$: BehaviorSubject<Language> = new BehaviorSubject(new Language());
  supportedLanguage$: BehaviorSubject<Language[]> = new BehaviorSubject([]);

  public set delegate(v: LanguageServiceDelegateInterface) {
    this._delegate = v;
  }

  // private fields
  private supportedLanguagesList = [
    { name: 'العربية', isoCode: LanguageEnum.Arabic } as Language,
    { name: 'English', isoCode: LanguageEnum.English } as Language,
  ];

  private _delegate: LanguageServiceDelegateInterface;

  getSupportedLanguagesList() {
    return this.supportedLanguagesList;
  }

  constructor(
    private translate: TranslateService,
    private cookieService: CookieService,
    private layoutService: LayoutService,
    private confirmationService: ConfirmationService,
    private directionality: Directionality
  ) {
    this.setupAppLanguageList();
    this.initialState();
    this.supportedLanguage$.subscribe((languages) => {
      if (!languages.length) { return; }
      this.initialState();
    });
  }

  changeLanguage(languageIsoCode: string) {
    if (this.currentLanguage$.value.isoCode === languageIsoCode) {
      return;
    }
    const language = this.supportedLanguagesList.find((lang) => languageIsoCode === lang.isoCode);
    if (language) {
      if (this._delegate) {
        this._delegate.willConfirmChangeLanguage().pipe(first()).subscribe((confirmTheChange) => {
          if (confirmTheChange) {
            this.confirmationService.confirm(
              new Confirmation(
                ConfirmationType.DestructiveAction,
                this.translationsList['ChangeLanguageConfirm']['Title'],
                this.translationsList['ChangeLanguageConfirm']['Message']
              )
            ).then(value => {
              if (value) {
                this.saveCurrentLanguage(language);
                this.languageChanged.emit(language);
              }
            });
          } else {
            this.saveCurrentLanguage(language);
            this.languageChanged.emit(language);
          }
        });
      } else {
        this.saveCurrentLanguage(language);
        this.languageChanged.emit(language);
      }
    }
  }

  checkLanguageDirection(languageIsoCode: string): string {
    if (languageIsoCode === 'ar') {
      return 'rtl';
    } else {
      return 'ltr';
    }
  }

  registerModule(locale: Locales): void {
    this.translate.onLangChange.pipe(
      startWith(true),
      map(() => this.translate.currentLang),
      filter((lang: string) => !!locale.phrasesPerLang[lang]),
    ).subscribe((lang) => {
      this.translate.setTranslation(
        lang,
        { [locale.modulePrefix]: locale.phrasesPerLang[lang] },
        true,
      );
    });
  }

  setOrganizationSupportedLanguage(supportedLanguages: Language[]) {
    this.supportedLanguagesList = [];
    this.supportedLanguagesList = supportedLanguages;
    this.supportedLanguage$.next(this.supportedLanguagesList);
  }

  private getLanguageConfirmationText() {
    this.translate.get(['ChangeLanguageConfirm']).subscribe((translations: any) => {
      this.translationsList['ChangeLanguageConfirm'] = translations['ChangeLanguageConfirm'];
    });
  }

  private saveCurrentLanguage(language: Language) {
    this.currentLanguage$.next(language);
    this.cookieService.set(
      ConfigConstant.APP_LANGUAGE_COOKIE_KEY,
      this.currentLanguage$.value.isoCode,
      new Date(new Date().setFullYear(new Date().getFullYear() + 1)),
      '/'
    );
    this.translate.use(this.currentLanguage$.value.isoCode).pipe(
      take(1),
      catchError(() => throwError(new AppError(['could not load language file isoCode: ' + this.currentLanguage$.value.isoCode])))
    ).subscribe();
    this.setWebSiteDirection();
  }

  private setupAppLanguageList() {
    const languagesArray: string[] = [];
    this.supportedLanguagesList.forEach((lang) => {
      languagesArray.push(lang.isoCode);
    });
    this.translate.addLangs(languagesArray);
  }

  private setWebSiteDirection() {
    const direction = this.checkLanguageDirection(this.currentLanguage$.value.isoCode);
    document.body.dir = direction;
    document.body.parentElement.dir = direction;
    document.body.firstElementChild.setAttribute('dir', direction);
    this.layoutService.websiteDirection$.next(direction);
    this.syncMaterialDirection(direction as Direction);
  }

  /**
   * Tells Angular Material which way the page runs.
   *
   * `Directionality` samples `document.body.dir` ONCE, in its own constructor, and never looks at the
   * DOM again. It is constructed before this service has applied the language's direction, so it
   * stays `'ltr'` for the whole session while the document says `rtl`. Material's stylesheets read the
   * live `[dir]` attribute, so CSS and JS end up disagreeing and every Material widget positioned
   * from TypeScript lays out for the wrong side.
   */
  private syncMaterialDirection(direction: Direction): void {
    if (this.directionality.value === direction) {
      return;
    }

    (this.directionality as { value: Direction }).value = direction;
    this.directionality.change.emit(direction);
  }

  private initialState() {
    const appLangIsoCode = this.cookieService.get(ConfigConstant.APP_LANGUAGE_COOKIE_KEY);
    let currentLanguage = new Language();
    if (appLangIsoCode) {
      currentLanguage = this.supportedLanguagesList.find((lang) => lang.isoCode === appLangIsoCode);
    } else {
      currentLanguage = this.supportedLanguagesList[0];
      this.saveCurrentLanguage(currentLanguage);
    }
    this.currentLanguage$.next(currentLanguage);
    this.translate.use(this.currentLanguage$.value.isoCode).pipe(
      take(1),
      catchError(() => throwError(new AppError(['could not load language file isoCode: ' + this.currentLanguage$.value.isoCode])))
    ).subscribe();
    this.translate.setDefaultLang(this.currentLanguage$.value.isoCode);
    this.setWebSiteDirection();
    this.getLanguageConfirmationText();
  }
}
